import { ExternalLink, Plus, RefreshCw, Sparkles } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select } from '@/components/ui/select';
import type {
  ProactivenessRollout,
  ProactivenessFilter,
} from '@/features/im-channel-proactivity-experiments/im-channel-proactivity-experiments-types';

const LEGACY_POSTHOG_URL =
  'https://us.posthog.com/project/100000/feature_flags?search=messaging-channel-proactive-routines';

type ExperimentsHeaderProps = {
  section: ProactivenessFilter;
  onSectionChange: (section: ProactivenessFilter) => void;
  rollouts: ProactivenessRollout[];
  hasExperiments: boolean;
  isSyncing: boolean;
  onCreate: () => void;
  onCreateRollout: () => void;
  onSync: () => void;
};

/** Explains cohort behavior and exposes the page-level controls. */
export const ExperimentsHeader = ({
  section,
  onSectionChange,
  rollouts,
  hasExperiments,
  isSyncing,
  onCreate,
  onCreateRollout,
  onSync,
}: ExperimentsHeaderProps) => {
  return (
    <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-4 space-y-0">
      <section className="space-y-1.5">
        <CardTitle className="flex items-center gap-2">
          <Sparkles className="h-5 w-5" />
          Proactive Routines
        </CardTitle>
        <CardDescription>
          PostHog controls who participates. Sync assigns users with a connected
          channel and, when integrations are declared, at least one connected
          integration. Shared prompt and schedule edits apply to all assigned
          copies.
        </CardDescription>
        <p className="text-sm text-muted-foreground">
          Channel selection is automatic: prefer iMessage, then WhatsApp. Users
          with neither are skipped.
        </p>
        {rollouts.length > 0 && (
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
            {rollouts.map((rollout) => {
              const posthogUrl =
                rollout.posthog_url ||
                (rollout.slug === 'im-channel-experimental'
                  ? LEGACY_POSTHOG_URL
                  : '');
              return /^https?:\/\//i.test(posthogUrl) ? (
                <a
                  key={rollout.slug}
                  href={posthogUrl}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="inline-flex items-center gap-1 underline"
                >
                  {rollout.slug} ({rollout.flag_key}){' '}
                  <ExternalLink className="h-3 w-3" />
                </a>
              ) : (
                <span key={rollout.slug}>
                  {rollout.slug} ({rollout.flag_key})
                </span>
              );
            })}
          </div>
        )}
      </section>
      <section className="flex flex-wrap items-center justify-end gap-2">
        <Select
          aria-label="View"
          className="w-auto"
          value={section}
          onChange={(event) =>
            onSectionChange(event.target.value as ProactivenessFilter)
          }
        >
          <option value="all">All</option>
          <option value="production">Production</option>
          <option value="experimental">Experiments</option>
        </Select>
        <Button
          variant="outline"
          onClick={onSync}
          disabled={isSyncing || !hasExperiments}
        >
          <RefreshCw className={isSyncing ? 'animate-spin' : undefined} />
          {isSyncing ? 'Syncing...' : 'Sync cohort'}
        </Button>
        <Button variant="outline" onClick={onCreateRollout}>
          <Plus />
          New rollout
        </Button>
        <Button onClick={onCreate} disabled={rollouts.length === 0}>
          <Plus />
          {section === 'experimental' ? 'New experiment' : 'New routine'}
        </Button>
      </section>
    </CardHeader>
  );
};
